import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../constants/Languages';
import {
  MARKETING_LANGUAGES,
  MARKETING_OG_IMAGE_HEIGHT,
  MARKETING_OG_IMAGE_WIDTH,
  MARKETING_OG_LOCALES,
  getLocalizedMarketingUrl,
  getMarketingAlternates,
  getMarketingOgImageUrl,
} from '../../constants/marketingSeo';
import { buildWebPageJsonLd } from '../../pages/marketing/landingContent';

/**
 * Head tags for an indexable marketing page. `path` is language-neutral (`/privacy`);
 * the canonical and hreflang URLs are derived per language. Mirrors the static head
 * written by scripts/generate-seo-pages.js, which these tags replace once React mounts.
 */
const PageSeo = ({ title, description, path = '/', jsonLd, noindex = false }) => {
  const { i18n } = useTranslation();
  const lang = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const canonical = getLocalizedMarketingUrl(path, lang);
  const imageUrl = getMarketingOgImageUrl(lang);
  const schemas =
    jsonLd == null
      ? buildWebPageJsonLd({ title, description, path, lang })
      : Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'} />
      <link rel="canonical" href={canonical} />
      {getMarketingAlternates(path).map(({ hrefLang, href }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Salovare" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={String(MARKETING_OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(MARKETING_OG_IMAGE_HEIGHT)} />
      <meta property="og:locale" content={MARKETING_OG_LOCALES[lang]} />
      {MARKETING_LANGUAGES.filter((code) => code !== lang).map((code) => (
        <meta key={code} property="og:locale:alternate" content={MARKETING_OG_LOCALES[code]} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      {schemas.map((schema) => (
        <script key={schema['@id'] || schema['@type']} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default PageSeo;
